import { Dispatch } from 'redux';
import fetchApi from '../services/api';
import * as types from './actionTypes';

/**
 * Get listings related to a listing
 * @param {number} mlsId - A listing's MLS ID
 * @param {boolean} comps - True if we want to fetch sold comps
 * @param {number} minBr - The minimum number of bedrooms properties should have
 * @param {number} maxBr - The maximum number of bedrooms properties should have
 * @param {number} minBath - The minimum number of bathrooms properties should have
 * @param {number} maxBath - The maximum number of bathrooms properties should have
 * @param {number} minPrice - The minimum price properties should have
 * @param {number} maxPrice - The maximum price properties should have
 * @param {number} minSf - The minimum square footage properties should have
 * @param {number} maxSf - The maximum square footage properties should have
 * @param {number} minUnits - The minimum number of units a multi-family property should have
 * @param {number} maxUnits - The maximum number of units a multi-family property should have
 * @param {number} radius - The area around the property in miles to search
 * @param {number} limit - The maximum number of properties to find
 * @param {number} daysOnMarket - Number of days on the market
 * @param {number} lastUpdatedMaxMonths - Number of months within which this must have been updated
 */
export function getRelatedListings(
  mlsId: number,
  comps = false,
  limit: number,
  minBath?: number,
  maxBath?: number,
  minBr?: number,
  maxBr?: number,
  minPrice?: number,
  maxPrice?: number,
  minSf?: number,
  maxSf?: number,
  minUnits?: number,
  maxUnits?: number,
  radius?: number,
  daysOnMarket?: number,
  lastUpdatedMaxMonths?: number,
) {
  return async (dispatch: Dispatch<any>) => {
    // Append any of the search params that we've passed
    const extraQueryParams = `${minBath ? `&minBath=${minBath}` : ''}${
      maxBath ? `&maxBath=${maxBath}` : ''
    }${minBr ? `&minBr=${minBr}` : ''}${maxBr ? `&maxBr=${maxBr}` : ''}${
      minPrice ? `&minPrice=${minPrice}` : ''
    }${maxPrice ? `&maxPrice=${maxPrice}` : ''}${
      minSf ? `&minSf=${minSf}` : ''
    }${maxSf ? `&maxSf=${maxSf}` : ''}${
      minUnits ? `&minUnits=${minUnits}` : ''
    }${maxUnits ? `&maxUnits=${maxUnits}` : ''}${
      radius ? `&radius=${radius}` : ''
    }${daysOnMarket ? `&daysOnMarket=${daysOnMarket}` : ''}${
      lastUpdatedMaxMonths
        ? `&lastUpdatedMaxMonths=${lastUpdatedMaxMonths}`
        : ''
    }${limit ? `&limit=${limit}` : ''}`;

    dispatch({
      type: types.ClearMessages,
    });
    if (comps) {
      dispatch({ type: types.CompsFetchStart });
    } else {
      dispatch({ type: types.RelatedListingsFetchStart });
    }

    try {
      const response = await fetchApi(
        `/listing/related/${mlsId}?comps=${comps}${extraQueryParams}`,
      );
      const json = await response.json();
      if (response.ok) {
        if (comps) {
          dispatch({
            comps: json.results,
            messages: [json],
            type: types.CompsFetchSuccess,
          });
        } else {
          dispatch({
            messages: [json],
            relatedListings: json.results,
            type: types.RelatedListingsFetchSuccess,
          });
        }
      } else {
        if (comps) {
          dispatch({
            messages: [json],
            type: types.CompsFetchFailure,
          });
        } else {
          dispatch({
            messages: [json],
            type: types.RelatedListingsFetchFailure,
          });
        }
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}
